import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, picUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Play, ArrowLeft, UserPlus, UserCheck, MessageCircle, Pencil, Trash2, Loader2, ShieldCheck, Bookmark, Heart } from "lucide-react";
import { toast } from "sonner";
import EditProfileDialog from "@/components/EditProfileDialog";
import ImageLightbox from "@/components/ImageLightbox";
import TopBoosters from "@/components/TopBoosters";
import LevelBadge from "@/components/LevelBadge";
import ProBadge from "@/components/ProBadge";
import { BadgeWithCelebration } from "@/components/CreatorBadge";
import { useTopBadges } from "@/lib/badges";

// Profile-page top-3 leaderboard badge — persistent (static, not the
// 10s-pulse video mode) since the profile is where creators put on
// display everything they've earned.
function ProfileTopBadge({ profileId, username }) {
  const badge = useTopBadges(profileId);
  if (!badge) return null;
  return (
    <BadgeWithCelebration
      category={badge.category}
      rank={badge.rank}
      username={username}
      size="md"
      mode="static"
    />
  );
}
import FollowListDialog from "@/components/FollowListDialog";
import CreatorSounds from "@/components/CreatorSounds";
import GridTilePreview from "@/components/GridTilePreview";

// Small inline modal for editing a video's product URL and/or caption.
// Rendered from Profile.jsx when the owner (or an admin) taps the pencil
// icon on a grid tile. Kept here so we don't spawn another file for a
// ~40-line component.
function EditVideoModal({ video, saving, onClose, onSave }) {
  const [productUrl, setProductUrl] = useState(video.product_url || "");
  const [caption, setCaption] = useState(video.caption || "");
  const urlOk = productUrl === "" ||
    /^https?:\/\/(www\.)?(guyanadiscounts\.com|aiwebmatrix\.online|play\.matrixshopping\.com)\//i.test(productUrl.trim());
  return (
    <div
      data-testid="edit-video-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={() => !saving && onClose()}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-[#0A0A0C] border border-[#39FF14]/40 shadow-[0_0_36px_rgba(57,255,20,0.25)]"
      >
        <div className="px-4 py-3 border-b border-white/10">
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#39FF14]">
            // edit.video
          </p>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#FF9500] mb-1.5">
              product url
            </p>
            <input
              type="url"
              value={productUrl}
              onChange={(e) => setProductUrl(e.target.value)}
              placeholder="Paste new guyanadiscounts.com URL — or clear to detach"
              data-testid="edit-product-url"
              className="w-full bg-white/5 border border-white/10 text-white placeholder:text-white/30 px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF9500]"
            />
            <p className={`mt-1 font-mono text-[9px] uppercase tracking-widest ${urlOk ? "text-white/40" : "text-[#FF3B30]"}`}>
              {urlOk
                ? (productUrl ? "supported domain ✓" : "leave blank to remove the shop link")
                : "only guyanadiscounts.com or aiwebmatrix.online"}
            </p>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">
              caption
            </p>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value.slice(0, 300))}
              rows={3}
              data-testid="edit-caption"
              className="w-full bg-white/5 border border-white/10 text-white placeholder:text-white/30 px-3 py-2.5 text-sm focus:outline-none focus:border-[#39FF14] resize-none"
            />
            <p className="mt-1 font-mono text-[9px] text-white/30">{caption.length}/300</p>
          </div>
        </div>
        <div className="grid grid-cols-2 divide-x divide-white/10 border-t border-white/10">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="py-3 font-mono text-[11px] uppercase tracking-widest text-white/60 hover:text-white disabled:opacity-40"
          >
            cancel
          </button>
          <button
            type="button"
            data-testid="edit-save-btn"
            onClick={() => onSave({ product_url: productUrl.trim(), caption: caption.trim() })}
            disabled={saving || !urlOk}
            className="py-3 font-mono text-[11px] uppercase tracking-widest text-black bg-[#39FF14] hover:shadow-[0_0_14px_rgba(57,255,20,0.55)] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? "saving…" : "save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Profile() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user, logout, setUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [followListMode, setFollowListMode] = useState(null); // "followers" | "following" | null
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Tabs — "posts" always; "saved" + "liked" only visible to the profile
  // owner. Both are private (bookmarks / likes reveal taste, we don't leak
  // them to other viewers). Loaded lazily on first tab switch.
  const [activeTab, setActiveTab] = useState("posts");
  const [savedVideos, setSavedVideos] = useState(null); // null = not yet fetched
  const [savedLoading, setSavedLoading] = useState(false);
  const [likedVideos, setLikedVideos] = useState(null);
  const [likedLoading, setLikedLoading] = useState(false);
  // "Videos I've shared" — this user's top curated picks (videos they've
  // driven the most clicks to via share links). Publicly visible on every
  // profile because it's reputation-building: sharers become mini-curators.
  const [sharedVideos, setSharedVideos] = useState([]);
  // Badges awarded to this user BY creators — public collection.
  const [badges, setBadges] = useState([]);
  const isSelf = user?.user_id === profile?.user_id;

  const requestDelete = (e, videoId) => {
    e.preventDefault();
    e.stopPropagation();
    setConfirmDeleteId(videoId);
  };

  const doDelete = async () => {
    if (!confirmDeleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/videos/${confirmDeleteId}`);
      setVideos((prev) => prev.filter((v) => v.id !== confirmDeleteId));
      setProfile((prev) =>
        prev ? { ...prev, video_count: Math.max(0, (prev.video_count || 1) - 1) } : prev
      );
      toast.success("Deleted");
      setConfirmDeleteId(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const [retryingIds, setRetryingIds] = useState(() => new Set());
  const [editingVideo, setEditingVideo] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const retryVideo = async (e, videoId) => {
    e.preventDefault();
    e.stopPropagation();
    if (retryingIds.has(videoId)) return;
    setRetryingIds((prev) => new Set(prev).add(videoId));
    try {
      await api.post(`/videos/${videoId}/retry-hls`);
      toast.success("Retry queued · refresh in about a minute");
      // Optimistically flip local state so the pill flips from FAILED to
      // RETRYING immediately — no need to wait for a refetch.
      setVideos((prev) =>
        prev.map((v) =>
          v.id === videoId
            ? {
                ...v,
                upload_status: {
                  ...(v.upload_status || {}),
                  state: "retrying",
                  message: "Rebuilding stream · attempt queued",
                  retry_attempts: 0,
                },
              }
            : v
        )
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Retry failed");
    } finally {
      setRetryingIds((prev) => {
        const next = new Set(prev);
        next.delete(videoId);
        return next;
      });
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [p, v, s, b] = await Promise.all([
        api.get(`/users/${username}`),
        api.get(`/users/${username}/videos`),
        api.get(`/users/${username}/shared-videos`).catch(() => ({ data: { items: [] } })),
        api.get(`/users/${username}/badges`).catch(() => ({ data: { items: [] } })),
      ]);
      setProfile(p.data);
      setVideos(v.data);
      setSharedVideos(s.data?.items || []);
      setBadges(b.data?.items || []);
    } catch {
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Reset tab state whenever the profile route changes so a switch between
    // /u/alice → /u/bob doesn't leak alice's saved/liked view onto bob.
    setActiveTab("posts");
    setSavedVideos(null);
    setLikedVideos(null);
  }, [username]);

  // Lazy-load saved videos the first time the owner opens the SAVED tab.
  // Refetches on toggle-back to keep the list fresh (a user might have saved
  // a new video from the feed since opening this page).
  useEffect(() => {
    if (activeTab !== "saved") return;
    if (!isSelf) return;
    let cancelled = false;
    setSavedLoading(true);
    api
      .get("/users/me/saved-videos")
      .then((r) => {
        if (!cancelled) setSavedVideos(r.data || []);
      })
      .catch(() => {
        if (!cancelled) setSavedVideos([]);
      })
      .finally(() => {
        if (!cancelled) setSavedLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, isSelf]);

  // Same lazy-load pattern for LIKED.
  useEffect(() => {
    if (activeTab !== "liked") return;
    if (!isSelf) return;
    let cancelled = false;
    setLikedLoading(true);
    api
      .get("/users/me/liked-videos")
      .then((r) => {
        if (!cancelled) setLikedVideos(r.data || []);
      })
      .catch(() => {
        if (!cancelled) setLikedVideos([]);
      })
      .finally(() => {
        if (!cancelled) setLikedLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, isSelf]);

  const toggleFollow = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    // When arriving from a notification, the URL slug is the actor's
    // user_id (see Notifications.jsx). Use the resolved profile's real
    // username for API actions — the follow endpoint doesn't accept
    // user_ids.
    const slug = profile?.username || username;
    try {
      if (profile.following) {
        await api.delete(`/users/${slug}/follow`);
        setProfile({
          ...profile,
          following: false,
          follower_count: Math.max(0, profile.follower_count - 1),
        });
      } else {
        await api.post(`/users/${slug}/follow`);
        setProfile({
          ...profile,
          following: true,
          follower_count: profile.follower_count + 1,
        });
      }
    } catch {
      toast.error("Could not update follow");
    }
  };

  const openConversation = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    // Same slug-normalization as toggleFollow — direct-message endpoint
    // resolves peers by username, not user_id.
    const slug = profile?.username || username;
    try {
      const r = await api.post("/messages/conversations", { username: slug });
      navigate(`/messages/${r.data.id}`);
    } catch {
      toast.error("Could not open chat");
    }
  };

  const impersonate = async () => {
    if (!profile?.username) return;
    if (
      !window.confirm(
        `Sign in as @${profile.username}? Your admin session will be paused; use the top banner to return.`
      )
    ) {
      return;
    }
    try {
      await api.post(`/admin/impersonate/${profile.username}`);
      toast.success(`Signed in as @${profile.username}`);
      // Full reload so every hook (feed, wallet, ws) rebuilds under the new user.
      window.location.href = "/";
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not sign in as this user"
      );
    }
  };

  if (loading) {
    return (
      <div className="md:ml-60 min-h-screen flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-[#39FF14] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="md:ml-60 min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF007F] mb-3">
          // 404
        </p>
        <h2 className="font-display font-black text-3xl mb-4">User not found</h2>
        <Link
          to="/"
          data-testid="profile-back-feed-btn"
          className="text-[#39FF14] font-mono text-xs uppercase tracking-widest hover:underline"
        >
          ← back to feed
        </Link>
      </div>
    );
  }

  return (
    <div
      className="md:ml-60 min-h-screen pb-24 md:pb-12"
      data-testid={`profile-page-${profile.username}`}
    >
      <div className="max-w-4xl mx-auto">
        {/* header */}
        <div
          data-testid="profile-header"
          className="px-6 pt-10 pb-8 border-b border-white/10"
        >
          <button
            onClick={() => navigate(-1)}
            data-testid="profile-back-btn"
            className="md:hidden flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-white/50 hover:text-white mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            back
          </button>

          <div className="flex flex-col sm:flex-row sm:items-start gap-6">
            <div className="relative">
              {profile.picture ? (
                <button
                  type="button"
                  onClick={() => setLightboxOpen(true)}
                  data-testid="profile-avatar-btn"
                  className="block group focus:outline-none"
                  aria-label="View profile picture"
                >
                  <img
                    src={picUrl(profile.picture)}
                    alt={profile.name}
                    className="h-28 w-28 object-cover border-2 border-[#39FF14] shadow-[0_0_25px_rgba(57,255,20,0.35)] group-hover:shadow-[0_0_40px_rgba(57,255,20,0.65)] transition-shadow cursor-zoom-in"
                  />
                </button>
              ) : (
                <div className="h-28 w-28 bg-[#FF007F]/20 flex items-center justify-center font-display font-black text-4xl text-[#FF007F] border-2 border-[#FF007F]/40">
                  {profile.name?.[0]?.toUpperCase()}
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">
                @{profile.username}
              </p>
              <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-3 flex items-center gap-3 flex-wrap">
                <span>{profile.name}</span>
                <ProBadge isPro={profile.is_pro} size="md" />
                <ProfileTopBadge profileId={profile.user_id} username={profile.username} />
              </h1>
              {profile.bio && (
                <p className="text-white/70 text-sm mb-4 max-w-lg">
                  {profile.bio}
                </p>
              )}

              {/* Level badges — both public, plus wallet balance chip */}
              {(profile.gifter?.level > 0 || profile.buyer?.level > 0 || (profile.wallet_balance_cents || 0) > 0) && (
                <div
                  data-testid="profile-levels"
                  className="flex items-center gap-6 mb-5 flex-wrap"
                >
                  {profile.gifter?.level > 0 && (
                    <div className="flex items-center gap-3">
                      <LevelBadge
                        level={profile.gifter.level}
                        kind="gifter"
                        size="md"
                        showLabel={false}
                      />
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 leading-tight">
                          gifter · L{profile.gifter.level} · {profile.gifter.tier?.name}
                        </p>
                        <p className="font-display font-bold text-white text-sm leading-tight">
                          ${((profile.lifetime_gifted_cents || 0) / 100).toFixed(2)} lifetime
                        </p>
                      </div>
                    </div>
                  )}
                  {profile.buyer?.level > 0 && (
                    <div className="flex items-center gap-3">
                      <LevelBadge
                        level={profile.buyer.level}
                        kind="buyer"
                        size="md"
                        showLabel={false}
                      />
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 leading-tight">
                          buyer · L{profile.buyer.level} · {profile.buyer.tier?.name}
                        </p>
                        <p className="font-display font-bold text-white text-sm leading-tight">
                          ${((profile.lifetime_purchased_cents || 0) / 100).toFixed(2)} lifetime
                        </p>
                      </div>
                    </div>
                  )}
                  {(profile.wallet_balance_cents || 0) > 0 && (
                    <div
                      data-testid="profile-wallet-balance"
                      className="flex items-center gap-3 pl-4 border-l border-white/10"
                    >
                      <div className="h-14 w-14 relative flex items-center justify-center">
                        <div
                          className="absolute inset-0"
                          style={{
                            background: "linear-gradient(135deg, #39FF14 0%, #00FFFF 50%, #39FF14 100%)",
                            clipPath: "polygon(50% 0, 100% 25%, 100% 75%, 50% 100%, 0 75%, 0 25%)",
                            boxShadow: "0 0 22px rgba(57,255,20,0.55), inset 0 0 12px rgba(0,0,0,0.4)",
                          }}
                        />
                        <div
                          className="absolute inset-[10%] flex items-center justify-center"
                          style={{
                            background: "#0A0A0C",
                            clipPath: "polygon(50% 0, 100% 25%, 100% 75%, 50% 100%, 0 75%, 0 25%)",
                          }}
                        >
                          <span
                            className="font-display font-black text-base"
                            style={{ color: "#39FF14", textShadow: "0 0 8px #39FF14" }}
                          >
                            $
                          </span>
                        </div>
                      </div>
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 leading-tight">
                          wallet · balance
                        </p>
                        <p
                          data-testid="profile-wallet-usd"
                          className="font-display font-bold text-white text-sm leading-tight"
                          style={{ textShadow: "0 0 8px rgba(57,255,20,0.25)" }}
                        >
                          ${((profile.wallet_balance_cents || 0) / 100).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center gap-6 mb-5">
                <div data-testid="stat-videos">
                  <p className="font-display font-bold text-xl">
                    {profile.video_count}
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                    posts
                  </p>
                </div>
                <div className="h-8 w-px bg-white/10" />
                <button
                  data-testid="stat-followers"
                  onClick={() => setFollowListMode("followers")}
                  className="text-left hover:opacity-80 transition-opacity"
                >
                  <p className="font-display font-bold text-xl group-hover:text-[#00FFFF]">
                    {profile.follower_count}
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                    followers
                  </p>
                </button>
                <div className="h-8 w-px bg-white/10" />
                <button
                  data-testid="stat-following"
                  onClick={() => setFollowListMode("following")}
                  className="text-left hover:opacity-80 transition-opacity"
                >
                  <p className="font-display font-bold text-xl">
                    {profile.following_count}
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                    following
                  </p>
                </button>
              </div>

              {profile.is_self ? (
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    onClick={() => setEditOpen(true)}
                    data-testid="profile-edit-btn"
                    className="bg-[#39FF14] text-black font-bold uppercase tracking-widest text-xs px-6 py-2.5 hover:shadow-[0_0_20px_rgba(57,255,20,0.5)] transition-all flex items-center gap-2"
                  >
                    <Pencil className="h-4 w-4" /> Edit profile
                  </button>
                  <button
                    onClick={logout}
                    data-testid="profile-logout-btn"
                    className="border border-white/15 hover:border-[#FF007F]/50 hover:text-[#FF007F] text-white/70 font-mono text-xs uppercase tracking-widest px-5 py-2.5 transition-all"
                  >
                    Sign out
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    onClick={toggleFollow}
                    data-testid="follow-btn"
                    className={`${
                      profile.following
                        ? "border border-white/20 text-white hover:border-[#FF007F]/50"
                        : "bg-[#39FF14] text-black hover:shadow-[0_0_20px_rgba(57,255,20,0.5)]"
                    } font-bold uppercase tracking-widest text-xs px-6 py-2.5 transition-all flex items-center gap-2`}
                  >
                    {profile.following ? (
                      <>
                        <UserCheck className="h-4 w-4" /> Following
                      </>
                    ) : (
                      <>
                        <UserPlus className="h-4 w-4" /> Follow
                      </>
                    )}
                  </button>
                  <button
                    onClick={openConversation}
                    data-testid="profile-message-btn"
                    className="border border-[#00FFFF]/40 hover:border-[#00FFFF] hover:bg-[#00FFFF]/10 text-[#00FFFF] font-bold uppercase tracking-widest text-xs px-6 py-2.5 transition-all flex items-center gap-2"
                  >
                    <MessageCircle className="h-4 w-4" /> Message
                  </button>
                  {user?.is_admin && (
                    <button
                      onClick={impersonate}
                      data-testid="profile-impersonate-btn"
                      title="Sign in as this user (admin only)"
                      className="border border-[#FF007F]/50 hover:border-[#FF007F] hover:bg-[#FF007F]/10 text-[#FF007F] font-bold uppercase tracking-widest text-xs px-5 py-2.5 transition-all flex items-center gap-2"
                    >
                      <ShieldCheck className="h-4 w-4" /> Sign in as
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* top boosters leaderboard */}
        <TopBoosters username={profile.username} />

        {/* Original sounds by this creator */}
        <CreatorSounds
          username={profile.username}
          isSelf={user?.user_id === profile.user_id}
        />

        {/* video grid */}
        <div className="px-2 sm:px-6 py-6">
          {/* Tabs — Saved only visible to the profile owner (bookmarks are private) */}
          {isSelf && (
            <div
              data-testid="profile-tabs"
              className="flex items-center gap-1 border-b border-white/10 mb-4 px-2 sm:px-0"
            >
              <button
                type="button"
                data-testid="profile-tab-posts"
                onClick={() => setActiveTab("posts")}
                className={`relative flex items-center gap-2 px-4 py-3 font-mono text-xs uppercase tracking-widest transition-colors ${
                  activeTab === "posts"
                    ? "text-[#39FF14]"
                    : "text-white/40 hover:text-white/70"
                }`}
              >
                <Play className="h-3.5 w-3.5" strokeWidth={2} />
                posts
                {activeTab === "posts" && (
                  <span className="absolute -bottom-px left-0 right-0 h-[2px] bg-[#39FF14] shadow-[0_0_8px_rgba(57,255,20,0.7)]" />
                )}
              </button>
              <button
                type="button"
                data-testid="profile-tab-saved"
                onClick={() => setActiveTab("saved")}
                className={`relative flex items-center gap-2 px-4 py-3 font-mono text-xs uppercase tracking-widest transition-colors ${
                  activeTab === "saved"
                    ? "text-[#00FFFF]"
                    : "text-white/40 hover:text-white/70"
                }`}
              >
                <Bookmark className="h-3.5 w-3.5" strokeWidth={2} />
                saved
                {activeTab === "saved" && (
                  <span className="absolute -bottom-px left-0 right-0 h-[2px] bg-[#00FFFF] shadow-[0_0_8px_rgba(0,255,255,0.7)]" />
                )}
              </button>
              <button
                type="button"
                data-testid="profile-tab-liked"
                onClick={() => setActiveTab("liked")}
                className={`relative flex items-center gap-2 px-4 py-3 font-mono text-xs uppercase tracking-widest transition-colors ${
                  activeTab === "liked"
                    ? "text-[#FF007F]"
                    : "text-white/40 hover:text-white/70"
                }`}
              >
                <Heart className="h-3.5 w-3.5" strokeWidth={2} />
                liked
                {activeTab === "liked" && (
                  <span className="absolute -bottom-px left-0 right-0 h-[2px] bg-[#FF007F] shadow-[0_0_8px_rgba(255,0,127,0.7)]" />
                )}
              </button>
            </div>
          )}

          {activeTab === "posts" && (
            videos.length === 0 ? (
              <div
                data-testid="profile-no-videos"
                className="py-20 text-center text-white/40"
              >
                <p className="font-display text-lg">No posts yet</p>
                <p className="font-mono text-xs mt-2 uppercase tracking-widest">
                  the feed is hungry
                </p>
              </div>
            ) : (
              <div
                data-testid="profile-video-grid"
                className="grid grid-cols-3 gap-1 sm:gap-2"
              >
                {videos.map((v) => (
                  <Link
                    key={v.id}
                    to={`/v/${v.id}?u=${encodeURIComponent(profile?.username || "")}`}
                    data-testid={`profile-video-${v.id}`}
                    className="aspect-[9/16] bg-white/5 relative group overflow-hidden"
                  >
                    <GridTilePreview video={v} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    {v.upload_status && v.upload_status.state !== "ready" && (() => {
                      const st = v.upload_status;
                      const colors = {
                        processing: { c: "#39FF14", label: "PROCESSING" },
                        playable_mp4: { c: "#00E5FF", label: "SD ONLY" },
                        retrying: { c: "#FFCC00", label: `RETRY ${st.retry_attempts + 1}/3` },
                        failed: { c: "#FF3B30", label: "FAILED" },
                      }[st.state] || { c: "#39FF14", label: "PROCESSING" };
                      const canRetry = st.state === "failed" && (isSelf || user?.is_admin);
                      const isRetrying = retryingIds.has(v.id);
                      return (
                        <div
                          data-testid={`profile-tile-status-${v.id}`}
                          className="absolute top-2 left-2 z-10 flex items-center gap-1 px-1.5 py-0.5 bg-black/80 border"
                          style={{ borderColor: `${colors.c}66` }}
                          title={st.message || ""}
                        >
                          <span
                            className="h-1 w-1 rounded-full animate-pulse"
                            style={{ backgroundColor: colors.c }}
                          />
                          <span
                            className="font-mono text-[8px] uppercase tracking-widest"
                            style={{ color: colors.c }}
                          >
                            {colors.label}
                          </span>
                          {canRetry && (
                            <button
                              type="button"
                              data-testid={`profile-tile-retry-${v.id}`}
                              onClick={(e) => retryVideo(e, v.id)}
                              disabled={isRetrying}
                              className="ml-1 font-mono text-[8px] uppercase tracking-widest text-black bg-[#39FF14] hover:shadow-[0_0_8px_rgba(57,255,20,0.55)] disabled:opacity-50 px-1.5 py-[1px] transition-shadow"
                            >
                              {isRetrying ? "…" : "RETRY"}
                            </button>
                          )}
                        </div>
                      );
                    })()}
                    {(isSelf || user?.is_admin) && (
                      <>
                        <button
                          type="button"
                          data-testid={`profile-video-edit-${v.id}`}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditingVideo(v); }}
                          title="Edit video"
                          className="absolute top-2 right-14 z-10 h-9 w-9 flex items-center justify-center border border-[#39FF14]/60 bg-black/80 text-[#39FF14] opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus:opacity-100 hover:bg-[#39FF14] hover:text-black active:bg-[#39FF14] active:text-black transition-all"
                        >
                          <Pencil className="h-4 w-4" strokeWidth={2.2} />
                        </button>
                        <button
                          type="button"
                          data-testid={`profile-video-delete-${v.id}`}
                          onClick={(e) => requestDelete(e, v.id)}
                          title="Delete video"
                          className="absolute top-2 right-2 z-10 h-9 w-9 flex items-center justify-center border border-[#FF007F]/60 bg-black/80 text-[#FF007F] opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus:opacity-100 hover:bg-[#FF007F] hover:text-black active:bg-[#FF007F] active:text-black transition-all"
                        >
                          <Trash2 className="h-4 w-4" strokeWidth={2.2} />
                        </button>
                      </>
                    )}
                    <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between text-white text-xs">
                      <span className="flex items-center gap-1 font-mono">
                        <Play className="h-3 w-3" fill="white" />
                        {v.displayed_view_count ?? v.view_count}
                      </span>
                      <span className="font-mono">♥ {v.like_count}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )
          )}

          {activeTab === "saved" && isSelf && (
            savedLoading && savedVideos === null ? (
              <div
                data-testid="profile-saved-loading"
                className="py-20 flex items-center justify-center"
              >
                <div className="h-6 w-6 border-2 border-[#00FFFF] border-t-transparent animate-spin" />
              </div>
            ) : (savedVideos || []).length === 0 ? (
              <div
                data-testid="profile-no-saved"
                className="py-20 text-center text-white/40"
              >
                <Bookmark className="h-8 w-8 mx-auto mb-4 text-[#00FFFF]/50" strokeWidth={1.5} />
                <p className="font-display text-lg">No saved videos yet</p>
                <p className="font-mono text-xs mt-2 uppercase tracking-widest max-w-xs mx-auto">
                  // tap the bookmark on any video to keep it here
                </p>
              </div>
            ) : (
              <div
                data-testid="profile-saved-grid"
                className="grid grid-cols-3 gap-1 sm:gap-2"
              >
                {savedVideos.map((v) => (
                  <Link
                    key={v.id}
                    to={`/v/${v.id}`}
                    data-testid={`profile-saved-video-${v.id}`}
                    className="aspect-[9/16] bg-white/5 relative group overflow-hidden"
                  >
                    <GridTilePreview video={v} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    <div className="absolute top-2 left-2 h-6 w-6 flex items-center justify-center bg-black/70 border border-[#00FFFF]/60 text-[#00FFFF]">
                      <Bookmark className="h-3 w-3" fill="#00FFFF" strokeWidth={0} />
                    </div>
                    <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between text-white text-xs">
                      <span className="flex items-center gap-1 font-mono">
                        <Play className="h-3 w-3" fill="white" />
                        {v.view_count}
                      </span>
                      <span className="font-mono truncate ml-2 text-[10px] text-white/60">
                        @{v.author?.username}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )
          )}

          {activeTab === "liked" && isSelf && (
            likedLoading && likedVideos === null ? (
              <div
                data-testid="profile-liked-loading"
                className="py-20 flex items-center justify-center"
              >
                <div className="h-6 w-6 border-2 border-[#FF007F] border-t-transparent animate-spin" />
              </div>
            ) : (likedVideos || []).length === 0 ? (
              <div
                data-testid="profile-no-liked"
                className="py-20 text-center text-white/40"
              >
                <Heart className="h-8 w-8 mx-auto mb-4 text-[#FF007F]/50" strokeWidth={1.5} />
                <p className="font-display text-lg">No liked videos yet</p>
                <p className="font-mono text-xs mt-2 uppercase tracking-widest max-w-xs mx-auto">
                  // double-tap or hit the heart on any video to keep it here
                </p>
              </div>
            ) : (
              <div
                data-testid="profile-liked-grid"
                className="grid grid-cols-3 gap-1 sm:gap-2"
              >
                {likedVideos.map((v) => (
                  <Link
                    key={v.id}
                    to={`/v/${v.id}`}
                    data-testid={`profile-liked-video-${v.id}`}
                    className="aspect-[9/16] bg-white/5 relative group overflow-hidden"
                  >
                    <GridTilePreview video={v} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    <div className="absolute top-2 left-2 h-6 w-6 flex items-center justify-center bg-black/70 border border-[#FF007F]/60 text-[#FF007F]">
                      <Heart className="h-3 w-3" fill="#FF007F" strokeWidth={0} />
                    </div>
                    <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between text-white text-xs">
                      <span className="flex items-center gap-1 font-mono">
                        <Play className="h-3 w-3" fill="white" />
                        {v.view_count}
                      </span>
                      <span className="font-mono truncate ml-2 text-[10px] text-white/60">
                        @{v.author?.username}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )
          )}

          {/* Creator-awarded badges — public collection of "curator awards"
              this user has received from other creators for driving traffic.
              Only rendered when non-empty. Powers the reward-loop visibility
              piece of share-to-earn. */}
          {badges.length > 0 && (
            <div className="mt-12" data-testid="profile-badges">
              <div className="flex items-baseline justify-between border-b border-[#FFD700]/25 pb-2 mb-4">
                <h2 className="font-display font-black uppercase tracking-wider text-white text-lg">
                  Curator <span className="text-[#FFD700]">Badges</span>
                </h2>
                <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                  {badges.length}
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {badges.map((b) => (
                  <div
                    key={b.id}
                    data-testid={`badge-${b.id}`}
                    className="p-3 bg-[#0A0A0C] border flex items-center gap-3"
                    style={{
                      borderColor: `${b.badge_color || "#39FF14"}55`,
                      boxShadow: `0 0 12px ${b.badge_color || "#39FF14"}22`,
                    }}
                  >
                    <span
                      className="text-3xl leading-none shrink-0"
                      aria-hidden="true"
                    >
                      {b.badge_icon}
                    </span>
                    <div className="min-w-0">
                      <p
                        className="font-display font-bold text-sm truncate"
                        style={{ color: b.badge_color || "#39FF14" }}
                      >
                        {b.badge_title}
                      </p>
                      {b.creator_username && (
                        <Link
                          to={`/u/${b.creator_username}`}
                          className="text-white/60 text-[10px] font-mono truncate block hover:text-white"
                        >
                          from @{b.creator_username}
                        </Link>
                      )}
                      <p className="text-white/40 text-[10px] font-mono">
                        + {b.coins_awarded} coins
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Videos this user has driven the most viewer clicks to via
              share links. Publicly visible on every profile — turns
              share-to-earn into a curator's reputation reel. Only rendered
              when there's actual data so brand-new accounts don't show
              an empty section. */}
          {sharedVideos.length > 0 && (
            <div className="mt-12" data-testid="profile-shared-videos">
              <div className="flex items-baseline justify-between border-b border-[#39FF14]/25 pb-2 mb-4">
                <h2 className="font-display font-black uppercase tracking-wider text-white text-lg">
                  Videos I&apos;ve <span className="text-[#39FF14]">Shared</span>
                </h2>
                <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                  {sharedVideos.length} pick{sharedVideos.length === 1 ? "" : "s"}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-1">
                {sharedVideos.map((v) => (
                  <Link
                    key={v.video_id}
                    to={`/v/${v.video_id}?ref=${encodeURIComponent(profile.username)}`}
                    data-testid={`shared-video-${v.video_id}`}
                    className="group relative aspect-[9/16] bg-black overflow-hidden"
                  >
                    <img
                      src={picUrl(v.poster_url)}
                      alt={v.caption || "shared video"}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                    />
                    {/* Bottom gradient + stats */}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent p-2 pt-8">
                      <p className="text-white text-xs font-medium line-clamp-2">
                        {v.caption || "\u00A0"}
                      </p>
                      <div className="flex items-center gap-2 mt-1 text-[10px] font-mono text-white/70">
                        <span className="flex items-center gap-0.5">
                          <Play className="h-2.5 w-2.5" fill="currentColor" strokeWidth={0} />
                          {(v.displayed_view_count ?? v.view_count).toLocaleString()}
                        </span>
                        <span className="text-[#39FF14]">
                          ↗ {v.share_visits}
                        </span>
                      </div>
                    </div>
                    {/* Creator handle chip — top-right */}
                    {v.creator_username && (
                      <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-black/70 backdrop-blur-md border border-white/15 font-mono text-[9px] text-white">
                        @{v.creator_username}
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <EditProfileDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        profile={profile}
        onSaved={(updated) => {
          setProfile({ ...profile, ...updated });
          setUser?.(updated);
        }}
      />
      <FollowListDialog
        open={followListMode !== null}
        onClose={() => setFollowListMode(null)}
        username={profile.username}
        mode={followListMode}
      />
      <ImageLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        src={picUrl(profile.picture)}
        alt={profile.name}
      />

      {/* Edit-video modal — owner (or admin) only. Changes product URL
          and/or caption. Backend re-scrapes the URL for fresh OG data,
          so no need to also send a new thumbnail. Passing an empty URL
          detaches the shop link entirely. */}
      {editingVideo && (
        <EditVideoModal
          video={editingVideo}
          saving={editSaving}
          onClose={() => !editSaving && setEditingVideo(null)}
          onSave={async ({ product_url, caption }) => {
            setEditSaving(true);
            try {
              const r = await api.patch(`/videos/${editingVideo.id}`, { product_url, caption });
              const fresh = r.data?.video || {};
              setVideos((prev) => prev.map((v) => v.id === editingVideo.id ? { ...v, ...fresh } : v));
              toast.success("Video updated");
              setEditingVideo(null);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Update failed");
            } finally {
              setEditSaving(false);
            }
          }}
        />
      )}

      {/* Delete-confirmation modal — owner only, click-through dismisses. */}
      {confirmDeleteId && (
        <div
          data-testid="delete-video-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => !deleting && setConfirmDeleteId(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm bg-[#0A0A0C] border border-[#FF007F]/50 shadow-[0_0_50px_rgba(255,0,127,0.35)]"
          >
            <div className="p-5 border-b border-white/10 flex items-center gap-2">
              <Trash2 className="h-4 w-4 text-[#FF007F]" strokeWidth={2.2} />
              <span
                className="font-display font-black text-lg text-[#FF007F] glitch"
                data-text="DELETE VIDEO"
              >
                DELETE VIDEO
              </span>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-white/80 text-sm">
                This will remove the video from your profile and every feed.
                Views, likes, comments, and any tips already received are
                kept in your history.
              </p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                // this cannot be undone
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="delete-video-cancel"
                  onClick={() => setConfirmDeleteId(null)}
                  disabled={deleting}
                  className="flex-1 border border-white/20 hover:border-white/60 text-white/70 hover:text-white font-mono text-xs uppercase tracking-widest py-2.5 transition-colors"
                >
                  cancel
                </button>
                <button
                  type="button"
                  data-testid="delete-video-confirm"
                  onClick={doDelete}
                  disabled={deleting}
                  className="flex-1 bg-[#FF007F] hover:shadow-[0_0_25px_rgba(255,0,127,0.6)] text-white font-bold uppercase tracking-widest text-xs py-2.5 transition-all disabled:opacity-50 inline-flex items-center justify-center gap-2"
                >
                  {deleting ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      deleting…
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={2.2} />
                      delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
